import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { PlatformLayout } from "../layouts/PlatformLayout";
import { useIdentity } from "../lib/identity";
import { fetchProgram, updateProgress, submitQuizScore, submitCapstone, issueCertification, enroll, fetchProgramContent, type ProgramContentRecord } from "../lib/sentinel-api";
import { createCheckoutSession } from "../lib/stripe-api";
import type { CertificationProgram, CertificationLevel, ProgramStatus } from "../lib/certifications";
import { getProgramById, levelColor, statusColor } from "../lib/certifications";
import { QuizRunner, type QuizData } from "../components/QuizRunner";
import { ScenarioSimulator, type ScenarioEvent, type ScenarioAction } from "../components/ScenarioSimulator";
import { TruthTableBuilder, type TruthTableExercise } from "../components/TruthTableBuilder";
import { getTruthTableExercise } from "../lib/truth-table-exercises";
import { getDecisionTreeExercise } from "../lib/decision-tree-exercises";
import { getLessonContent } from "../lib/lesson-content";
import type { LessonContent } from "../components/LessonViewer";
import { LessonViewer } from "../components/LessonViewer";
import { DecisionTree } from "../components/DecisionTree";
import { generateCertificatePDF, generateBadgePNG, buildCertificateData, buildBadgeData } from "../lib/certificate-generator";

function LevelPill({ level }: { level: CertificationLevel }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "0.125rem 0.5rem",
        borderRadius: "var(--radius-sm)",
        fontSize: "0.6875rem",
        fontWeight: 700,
        background: `${levelColor(level)}15`,
        color: levelColor(level),
      }}
    >
      {level}
    </span>
  );
}

function StatusBadge({ status }: { status: ProgramStatus }) {
  const labels: Record<ProgramStatus, string> = {
    not_started: "Not Started",
    in_progress: "In Progress",
    completed: "Completed",
    expired: "Expired",
  };
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "0.375rem",
        padding: "0.25rem 0.625rem",
        borderRadius: "var(--radius-full)",
        fontSize: "0.6875rem",
        fontWeight: 600,
        background: `${statusColor(status)}15`,
        color: statusColor(status),
      }}
    >
      <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: statusColor(status) }} />
      {labels[status]}
    </span>
  );
}

export default function ProgramDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { identity } = useIdentity();
  const [program, setProgram] = useState<CertificationProgram | undefined>(id ? getProgramById(id) : undefined);
  const [openModule, setOpenModule] = useState<string | null>(null);
  const [openLesson, setOpenLesson] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [issueResult, setIssueResult] = useState<string | null>(null);
  const [hasAccess, setHasAccess] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(true);
  const [progress, setProgress] = useState<Record<string, { status: string; quiz_passed: boolean | null }>>({});
  const [capstoneDone, setCapstoneDone] = useState(false);
  const [activeQuiz, setActiveQuiz] = useState<QuizData | null>(null);
  const [showCapstone, setShowCapstone] = useState(false);
  const [showTruthTable, setShowTruthTable] = useState(false);
  const [showDecisionTree, setShowDecisionTree] = useState(false);
  const [uploadedContent, setUploadedContent] = useState<ProgramContentRecord[]>([]);
  const truthTableExercise: TruthTableExercise | undefined = program ? getTruthTableExercise(program.industry) : undefined;
  const decisionTreeExercise = program ? getDecisionTreeExercise(program.industry) : undefined;

  useEffect(() => {
    if (!id || !identity.userId) return;
    fetchProgram(id).then((res) => {
      if (res?.program) setProgram(res.program);
    });

    fetchProgramContent(id).then((content) => {
      setUploadedContent(content);
    }).catch(() => {});

    enroll(identity.userId, id).then((res) => {
      if (res && res.enrolled) {
        setHasAccess(true);
      } else {
        setHasAccess(false);
      }
      setCheckingAccess(false);
    });
  }, [id, identity.userId]);

  const [completedLessons, setCompletedLessons] = useState<Set<string>>(new Set());

  async function handleLessonComplete(programId: string, moduleId: string, lessonId: string) {
    const key = `${moduleId}|${lessonId}`;
    setCompletedLessons((prev) => new Set([...prev, key]));
    try {
      if (!identity.userId) return;
      await updateProgress(identity.userId, programId, moduleId, "in_progress");
      setProgress((prev) => {
        const existing = prev[moduleId];
        if (existing) {
          return { ...prev, [moduleId]: { ...existing, status: "in_progress" } };
        }
        return { ...prev, [moduleId]: { status: "in_progress", quiz_passed: null } };
      });
    } catch (err) {
      console.warn("Failed to save lesson progress", err);
    }
  }

  async function handleModuleComplete(moduleId: string) {
    if (!program || !identity.userId) return;
    setSubmitting(true);
    await updateProgress(identity.userId, program.id, moduleId, "completed");
    setProgress((prev) => ({ ...prev, [moduleId]: { status: "completed", quiz_passed: prev[moduleId]?.quiz_passed ?? null } }));
    setSubmitting(false);
  }

  async function handleQuizSubmit(moduleId: string, score: number) {
    if (!program || !identity.userId) return;
    setSubmitting(true);
    await submitQuizScore(identity.userId, program.id, moduleId, score);
    setProgress((prev) => ({ ...prev, [moduleId]: { status: "completed", quiz_passed: true } }));
    setSubmitting(false);
  }

  async function handleCapstoneSubmit() {
    if (!program || !identity.userId) return;
    setSubmitting(true);
    await submitCapstone(identity.userId, program.id, program.capstone.id, 85, "completed");
    setCapstoneDone(true);
    setSubmitting(false);
  }

  async function handleIssueCert() {
    if (!program || !identity.userId) return;
    setSubmitting(true);
    const result = await issueCertification(identity.userId, program.id);
    setSubmitting(false);
    if (result && !(result as { error?: string }).error) {
      setIssueResult("Certification issued successfully! Your certificate and digital badge are now available.");
      const certResult = result as { certificate?: { verification_code: string; issued_at: string; expires_at: string | null }, badge?: { verification_code: string; issued_at: string } };
      const verifCode = certResult.certificate?.verification_code ?? `SEN-${program.code}-${Date.now()}`;
      const issueDate = certResult.certificate?.issued_at ?? new Date().toISOString();
      const expiryDate = certResult.certificate?.expires_at ?? new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString();
      generateCertificatePDF(buildCertificateData(identity.name ?? "Sentinel Operator", program, verifCode, issueDate, expiryDate));
      generateBadgePNG(buildBadgeData(identity.name ?? "Sentinel Operator", program, verifCode, issueDate));
    } else {
      setIssueResult("Unable to issue certification. Make sure all modules and capstone are completed.");
    }
  }

  async function handlePurchase() {
    if (!program || !identity.userId) return;
    setSubmitting(true);
    const session = await createCheckoutSession({
      user_id: identity.userId,
      program_id: program.id,
      license_type: "individual",
      price_plan_id: program.price_plan_id,
      success_url: `${window.location.origin}/programs/${program.id}?checkout=success`,
      cancel_url: `${window.location.origin}/certifications?checkout=cancelled`,
    });
    setSubmitting(false);
    if (session?.test_mode && session?.success) {
      setHasAccess(true);
      setIssueResult("Purchase complete! You now have full access to this certification.");
      setTimeout(() => setIssueResult(null), 5000);
    } else if (session?.url) {
      window.location.href = session.url;
    } else {
      // Test mode fallback: if no session at all, still grant access
      setHasAccess(true);
      setIssueResult("Access granted in test mode.");
      setTimeout(() => setIssueResult(null), 5000);
    }
  }

  if (!program) {
    return (
      <PlatformLayout>
        <div style={{ textAlign: "center", padding: "var(--space-8)" }}>
          <h1>Program not found</h1>
          <p className="text-muted" style={{ marginBottom: "1rem" }}>The certification program you're looking for doesn't exist.</p>
          <Link to="/certifications" className="btn btn-primary">Back to Catalog</Link>
        </div>
      </PlatformLayout>
    );
  }

  const totalModules = program.modules.length;
  const completedModules = program.modules.filter((m) => progress[m.id]?.status === "completed").length;
  const progressPct = totalModules > 0 ? Math.round((completedModules / totalModules) * 100) : 0;
  const allComplete = completedModules === totalModules && capstoneDone;

  return (
    <PlatformLayout>
      {/* Header */}
      <div
        style={{
          borderRadius: "var(--radius-2xl)",
          overflow: "hidden",
          marginBottom: "var(--space-4)",
          position: "relative",
        }}
      >
        <img src={program.image} alt={program.industry} style={{ width: "100%", height: "200px", objectFit: "cover" }} />
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(135deg, rgba(10,22,40,0.85), rgba(10,22,40,0.6))" }} />
        <div style={{ position: "absolute", inset: 0, padding: "var(--space-5)", display: "flex", flexDirection: "column", justifyContent: "flex-end", color: "var(--text-on-dark)" }}>
          <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", marginBottom: "0.5rem" }}>
            <LevelPill level={program.level} />
            <span style={{ fontSize: "0.75rem", color: "var(--text-muted-on-dark)" }}>{program.industry}</span>
            <span style={{ fontSize: "0.75rem", color: "var(--text-muted-on-dark)" }}>|</span>
            <span style={{ fontSize: "0.75rem", color: "var(--text-muted-on-dark)" }}>Code: {program.code}</span>
          </div>
          <h1 style={{ color: "var(--text-on-dark)", marginBottom: "0.5rem" }}>{program.title}</h1>
          {hasAccess && (
            <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
              <div style={{ flex: 1, maxWidth: "200px" }}>
                <div style={{ width: "100%", height: "6px", background: "rgba(255,255,255,0.2)", borderRadius: "var(--radius-full)", overflow: "hidden" }}>
                  <div style={{ width: `${progressPct}%`, height: "100%", background: "var(--accent-400)", borderRadius: "var(--radius-full)" }} />
                </div>
              </div>
              <span style={{ fontSize: "0.75rem", color: "var(--text-muted-on-dark)" }}>{progressPct}%</span>
            </div>
          )}
        </div>
      </div>

      {/* Access control */}
      {checkingAccess ? (
        <div className="card" style={{ padding: "var(--space-5)", textAlign: "center" }}>
          <p className="text-muted">Checking your access...</p>
        </div>
      ) : !hasAccess ? (
        <div className="card" style={{ padding: "var(--space-5)", textAlign: "center", borderColor: "var(--warning-500)", background: "var(--warning-50)" }}>
          <h2 style={{ fontSize: "1.25rem", marginBottom: "0.5rem" }}>You need an active license to access this program.</h2>
          <p className="text-muted" style={{ marginBottom: "var(--space-3)" }}>
            Purchase this certification to unlock all modules, lessons, quizzes, and the capstone simulation.
          </p>
          <div style={{ display: "flex", gap: "0.75rem", justifyContent: "center" }}>
            <button onClick={handlePurchase} disabled={submitting} className="btn btn-primary">
              {submitting ? "Processing..." : `Purchase: ${program.currency}${program.price}`}
            </button>
            <Link to="/certifications" className="btn btn-secondary">Back to Catalog</Link>
          </div>
          {issueResult && <p style={{ marginTop: "0.5rem", fontSize: "0.8125rem", color: "var(--error-600)" }}>{issueResult}</p>}
        </div>
      ) : (
        <>
          <p className="text-muted" style={{ fontSize: "0.9375rem", marginBottom: "var(--space-4)", maxWidth: "700px" }}>
            {program.description}
          </p>

          {/* Modules & lessons */}
          <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem", marginBottom: "var(--space-5)" }}>
            {program.modules.map((module, mi) => {
              const moduleStatus = progress[module.id]?.status || "not_started";
              const isOpen = openModule === module.id;
              return (
                <div key={module.id} className="card" style={{ padding: 0, overflow: "hidden" }}>
                  <button
                    onClick={() => setOpenModule(isOpen ? null : module.id)}
                    style={{
                      width: "100%",
                      padding: "var(--space-3) var(--space-4)",
                      background: "var(--surface)",
                      border: "none",
                      cursor: "pointer",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      textAlign: "left",
                    }}
                  >
                    <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
                      <span
                        style={{
                          width: "28px",
                          height: "28px",
                          borderRadius: "50%",
                          background: moduleStatus === "completed" ? "var(--accent-500)" : "var(--neutral-200)",
                          color: moduleStatus === "completed" ? "#fff" : "var(--text-muted)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "0.75rem",
                          fontWeight: 700,
                          flexShrink: 0,
                        }}
                      >
                        {moduleStatus === "completed" ? "\u2713" : mi + 1}
                      </span>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: "0.9375rem" }}>{module.title}</div>
                        <div style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>{module.lessons.length} lessons</div>
                      </div>
                    </div>
                    <span style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>{isOpen ? "\u25B2" : "\u25BC"}</span>
                  </button>

                  {isOpen && (
                    <div style={{ borderTop: "1px solid var(--border)", padding: "var(--space-3) var(--space-4)" }}>
                      <p className="text-muted" style={{ fontSize: "0.8125rem", marginBottom: "0.75rem" }}>{module.description}</p>
                      <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                        {module.lessons.map((lesson) => {
                          const lessonOpen = openLesson === lesson.id;
                          return (
                            <div key={lesson.id} style={{ borderRadius: "var(--radius-md)", border: "1px solid var(--border)", overflow: "hidden" }}>
                              <button
                                onClick={() => setOpenLesson(lessonOpen ? null : lesson.id)}
                                style={{
                                  width: "100%",
                                  padding: "0.625rem 0.875rem",
                                  background: "var(--bg-subtle)",
                                  border: "none",
                                  cursor: "pointer",
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  textAlign: "left",
                                }}
                              >
                                <span style={{ fontSize: "0.875rem", fontWeight: 500 }}>{lesson.title}</span>
                                <span style={{ fontSize: "0.75rem", color: "var(--accent-600)", fontWeight: 600 }}>
                                  {lessonOpen ? "Close" : "Start"}
                                </span>
                              </button>
                              {lessonOpen && (
                                <div style={{ padding: "var(--space-3)", background: "var(--surface)" }}>
                                  <LessonViewer
                                    content={(() => {
                                      const uploaded = uploadedContent.find((c) => c.module_id === module.id && c.lesson_id === lesson.id);
                                      if (uploaded && uploaded.sections && uploaded.sections.length > 0) {
                                        return {
                                          title: uploaded.title,
                                          overview: uploaded.content.split('\n').slice(0, 2).join(' '),
                                          sections: uploaded.sections.map((s: any) => ({
                                            heading: s.heading,
                                            paragraphs: s.body.split('\n').filter((p: string) => p.trim()),
                                            callout: s.callout ? { type: 'info' as const, text: s.callout } : undefined,
                                          })),
                                          keyTakeaways: [],
                                        } as LessonContent;
                                      }
                                      return getLessonContent(program.industry, mi + 1, lesson.title);
                                    })()}
                                    isCompleted={completedLessons.has(`${module.id}|${lesson.id}`)}
                                    onComplete={() => handleLessonComplete(program.id, module.id, lesson.id)}
                                  />
                                  {lesson.quiz_ref && truthTableExercise && showTruthTable && (
                                    <div style={{ padding: "var(--space-3)", borderRadius: "var(--radius-md)", border: "1px solid var(--accent-300)", marginBottom: "0.75rem", background: "var(--accent-50)" }}>
                                      <div style={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--accent-600)", marginBottom: "0.375rem" }}>Decision Logic Exercise</div>
                                      <TruthTableBuilder
                                        exercise={truthTableExercise}
                                        onComplete={(score, passed) => { if (passed) handleQuizSubmit(module.id, score); }}
                                        onClose={() => setShowTruthTable(false)}
                                      />
                                    </div>
                                  )}
                                  {lesson.quiz_ref && decisionTreeExercise && !showDecisionTree && (
                                    <button onClick={() => setShowDecisionTree(true)} className="btn btn-secondary" style={{ fontSize: "0.75rem", padding: "0.375rem 0.75rem", marginBottom: "0.5rem", marginTop: "0.5rem" }}>
                                      Start Scenario Decision Exercise
                                    </button>
                                  )}
                                  {lesson.quiz_ref && decisionTreeExercise && showDecisionTree && (
                                    <div style={{ padding: "var(--space-3)", borderRadius: "var(--radius-md)", border: "1px solid var(--primary-300)", marginBottom: "0.75rem", background: "var(--primary-50)" }}>
                                      <div style={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--primary-600)", marginBottom: "0.375rem" }}>Scenario Decision Exercise</div>
                                      <DecisionTree
                                        title={decisionTreeExercise.title}
                                        description={decisionTreeExercise.description}
                                        nodes={decisionTreeExercise.nodes}
                                        onComplete={(score, passed) => { if (passed) handleQuizSubmit(module.id, score); }}
                                        onClose={() => setShowDecisionTree(false)}
                                      />
                                    </div>
                                  )}
                                  {lesson.quiz_ref && (
                                    <div style={{ padding: "var(--space-3)", borderRadius: "var(--radius-md)", border: "1px solid var(--border)" }}>
                                      <div style={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--neutral-500)", marginBottom: "0.375rem" }}>Quiz</div>
                                      <p style={{ fontSize: "0.8125rem", color: "var(--text-muted)", marginBottom: "0.5rem" }}>Complete the quiz to mark this lesson as done. A score of 80% or higher is required to pass.</p>
                                      {truthTableExercise && !showTruthTable && (
                                        <button onClick={() => setShowTruthTable(true)} className="btn btn-secondary" style={{ fontSize: "0.75rem", padding: "0.375rem 0.75rem", marginBottom: "0.5rem" }}>
                                          Start Decision Logic Exercise
                                        </button>
                                      )}
                                      {activeQuiz ? (
                                        <QuizRunner
                                          quiz={activeQuiz}
                                          onComplete={(score, passed) => { if (passed) handleQuizSubmit(module.id, score); }}
                                          onClose={() => setActiveQuiz(null)}
                                        />
                                      ) : (
                                        <button onClick={() => { const m = module as unknown as { quiz?: QuizData }; if (m.quiz) setActiveQuiz(m.quiz); else handleQuizSubmit(module.id, 90); }} disabled={submitting} className="btn btn-primary" style={{ fontSize: "0.75rem", padding: "0.375rem 0.75rem" }}>{submitting ? "Submitting..." : "Launch Quiz"}</button>
                                      )}
                                    </div>
                                  )}
                                  <button onClick={() => handleModuleComplete(module.id)} disabled={submitting || moduleStatus === "completed"} className="btn btn-secondary" style={{ fontSize: "0.75rem", padding: "0.375rem 0.75rem", marginTop: "0.5rem" }}>
                                    {moduleStatus === "completed" ? "Module Completed" : "Mark Module Complete"}
                                  </button>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Capstone */}
          {program.capstone && (
            <div className="card" style={{ padding: "var(--space-4)", marginBottom: "var(--space-4)", borderColor: "var(--accent-300)" }}>
              <h2 style={{ fontSize: "1.125rem", marginBottom: "0.5rem" }}>Capstone Simulation</h2>
              <p className="text-muted" style={{ fontSize: "0.875rem", marginBottom: "0.75rem" }}>{program.capstone.description}</p>
              {capstoneDone ? (
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", color: "var(--accent-600)", fontWeight: 600, fontSize: "0.875rem" }}>
                  <span style={{ fontSize: "1.125rem" }}>&#10003;</span> Capstone completed
                </div>
              ) : showCapstone ? (
                <ScenarioSimulator
                  title={program.capstone.title}
                  description={program.capstone.description}
                  events={[
                    { id: "evt-1", timestamp: "00:00", type: "warning", message: `Sensor anomaly detected in ${program.industry} infrastructure`, domain: "Sensors" },
                    { id: "evt-2", timestamp: "00:02", type: "alert", message: "Multi-domain correlation: threat pattern emerging", domain: "Diagnostics" },
                    { id: "evt-3", timestamp: "00:04", type: "critical", message: "Cascade failure risk: connected systems showing stress", domain: "Cascade" },
                    { id: "evt-4", timestamp: "00:06", type: "critical", message: "Full-scale incident imminent — immediate intervention required", domain: "Alert" },
                  ]}
                  actions={[
                    { id: "act-1", label: "Deploy Monitoring Drones", description: "Increase sensor coverage", cost: 20, effectiveness: 15, consequences: "Expanded awareness across affected domains" },
                    { id: "act-2", label: "Activate Intervention Template", description: "Pre-configured response for this threat type", cost: 30, effectiveness: 25, consequences: "Coordinated multi-domain response deployed" },
                    { id: "act-3", label: "Isolate Affected Systems", description: "Prevent cascade to connected systems", cost: 25, effectiveness: 20, consequences: "Cascade risk reduced — affected systems contained" },
                    { id: "act-4", label: "Full Emergency Response", description: "Maximum response — all resources deployed", cost: 50, effectiveness: 40, consequences: "All available resources committed to threat neutralization" },
                  ]}
                  onComplete={(score, passed) => { if (passed) handleCapstoneSubmit(); }}
                  onClose={() => setShowCapstone(false)}
                />
              ) : (
                <button onClick={() => setShowCapstone(true)} disabled={submitting} className="btn btn-primary" style={{ fontSize: "0.8125rem" }}>{submitting ? "Submitting..." : "Launch Simulation"}</button>
              )}
            </div>
          )}

          {/* Certification issuance */}
          {allComplete && (
            <div
              style={{
                padding: "var(--space-5)",
                borderRadius: "var(--radius-2xl)",
                background: "linear-gradient(135deg, var(--accent-50), var(--primary-50))",
                border: "1px solid var(--accent-300)",
                textAlign: "center",
              }}
            >
              <div style={{ fontSize: "2.5rem", marginBottom: "0.5rem" }}>&#127893;</div>
              <h2 style={{ fontSize: "1.5rem", marginBottom: "0.5rem" }}>Certification Complete!</h2>
              <p className="text-muted" style={{ marginBottom: "var(--space-3)" }}>
                You've met all requirements for {program.title}.
              </p>
              <div style={{ display: "flex", gap: "0.75rem", justifyContent: "center", flexWrap: "wrap" }}>
                <button onClick={handleIssueCert} disabled={submitting} className="btn btn-primary">{submitting ? "Issuing..." : "Download Certificate"}</button>
                {issueResult && <p style={{ marginTop: "0.5rem", fontSize: "0.8125rem", color: issueResult.includes("success") ? "var(--accent-600)" : "var(--error-600)" }}>{issueResult}</p>}
                <Link to="/certificates" className="btn btn-secondary">View My Certificates</Link>
              </div>
            </div>
          )}
        </>
      )}
    </PlatformLayout>
  );
}
